import React from "react";
import StoreAction from "../constants/store-action";
import Slider from "../components/slider";
import Alert from "../components/alert";
import SpinnerInline from "../components/spinner-inline";
import { validateStringNumberInRange } from "../utils/validation";
import { addSizeQuery } from "../utils/image";
import { formatPrice, formatPercentage } from "../utils/helpers";
import { getLoanDetails } from "../utils/payment";
import { getConvertedResidualSpecification, isResidualEnabled, isResidualFixed, } from "../utils/residual";
import { validatePayment } from "../tools/data-validation";
import { PaymentType, } from "@wayke-se/ecom";
import UserEvent from "../constants/user-event";
import HelperBoxLabel from "../components/helper-box-label";
import HelperLabel from "../components/helper-label";
import { getRetailerInformation } from "../utils/retailer";
import shouldUseCreditAssessment from "../utils/credit-assessment/usage-resolver";
const ProceedButton = ({ updating, onClick }) => (React.createElement("div", { "data-ecom-buttonnav": "" },
    React.createElement("div", { className: "button-nav-item" },
        React.createElement("button", { "data-ecom-button": "full-width", disabled: updating, onClick: onClick }, !updating ? ("Gå vidare") : (React.createElement(React.Fragment, null,
            React.createElement("div", { "data-ecom-spinner": "inline", className: "m-r-half" },
                React.createElement("div", { className: "spinner" })),
            "Uppdaterar"))))));
const getAllDurationSteps = (durationSpecification) => {
    const difference = durationSpecification.max - durationSpecification.min;
    const numberOfSteps = difference / durationSpecification.step;
    const result = [];
    for (let i = 0; i <= numberOfSteps; i += 1) {
        const value = durationSpecification.min + i * durationSpecification.step;
        result.push(value);
    }
    return result;
};
const getIndexFromDuration = (duration, durationSpecification) => getAllDurationSteps(durationSpecification).findIndex((s) => s === duration);
const getDurationFromIndex = (index, durationSpecification) => getAllDurationSteps(durationSpecification).find((_, i) => i === index);
const createFinancialProviderLink = (loanDetails, title) => {
    let url = loanDetails.getPublicURL();
    if (!url) {
        return "";
    }
    const titleParam = `title=${title}`;
    url = url.includes("?") ? `${url}&${titleParam}` : `${url}?${titleParam}`;
    return encodeURI(url);
};
class PaymentFinancingDetails extends React.Component {
    constructor(props) {
        super(props);
        this.handleDetailsClick = this.handleDetailsClick.bind(this);
        this.handleDurationChange = this.handleDurationChange.bind(this);
        this.handleInputValueChange = this.handleInputValueChange.bind(this);
        this.handleSliderChange = this.handleSliderChange.bind(this);
        this.handleValueUpdated = this.handleValueUpdated.bind(this);
        this.handleProceedClick = this.handleProceedClick.bind(this);
        this.updateStoreValues = this.updateStoreValues.bind(this);
        const loanDetails = getLoanDetails(props.orderOptions, props.paymentLookup);
        const convertedResidualSpecification = getConvertedResidualSpecification(loanDetails.getResidualValueSpec());
        const hasResidual = isResidualEnabled(convertedResidualSpecification);
        let residual = null;
        if (hasResidual) {
            residual = props.data.payment.loanResidual
                ? props.data.payment.loanResidual * 100
                : convertedResidualSpecification.default;
        }
        const deposit = props.data.payment.loanDeposit
            ? props.data.payment.loanDeposit
            : loanDetails.getDownPaymentSpec().default;
        const duration = props.data.payment.loanDuration
            ? props.data.payment.loanDuration
            : loanDetails.getDurationSpec().default;
        const durationIndex = getIndexFromDuration(duration, loanDetails.getDurationSpec());
        this.state = {
            durationIndex,
            isShowingDetails: false,
            hasRequestError: false,
            deposit: `${deposit}`,
            residual: residual === null ? null : `${residual}`,
        };
    }
    handleDetailsClick() {
        this.setState({
            isShowingDetails: !this.state.isShowingDetails,
        });
    }
    handleDurationChange(e) {
        this.setState({
            durationIndex: e.target.selectedIndex,
        }, () => {
            this.handleValueUpdated();
        });
    }
    handleInputValueChange(e) {
        this.setState({
            [e.target.name]: e.target.value,
        });
    }
    handleSliderChange(name, value) {
        this.setState({
            [name]: value,
        });
    }
    handleValueUpdated() {
        this.updateStoreValues((state) => {
            const isValidPayment = validatePayment(state.payment, this.props.orderOptions, this.props.paymentLookup);
            if (!isValidPayment) {
                return;
            }
            this.props.onFetchPaymentInformation((isSuccessful) => {
                this.setState({
                    hasRequestError: !isSuccessful,
                });
            });
        });
    }
    handleProceedClick() {
        this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
            type: "payment",
            name: "hasAcceptedLoanDetails",
            value: true,
        }, () => {
            this.updateStoreValues((state) => {
                const isValidPayment = validatePayment(state.payment, this.props.orderOptions, this.props.paymentLookup);
                if (!isValidPayment) {
                    return;
                }
                this.props.onIncompleteUserEvent(UserEvent.PAYMENT_TYPE_LOAN_CHOSEN);
                this.props.onFetchPaymentInformation((isSuccessful) => {
                    if (isSuccessful) {
                        this.setState({
                            hasRequestError: false,
                        });
                        this.props.onProceedToNextStep();
                    }
                    else {
                        this.setState({
                            hasRequestError: true,
                        });
                    }
                });
            });
        });
    }
    updateStoreValues(callback) {
        const loanDetails = getLoanDetails(this.props.orderOptions, this.props.paymentLookup);
        const depositSpecification = loanDetails.getDownPaymentSpec();
        const durationSpecification = loanDetails.getDurationSpec();
        const convertedResidualSpecification = getConvertedResidualSpecification(loanDetails.getResidualValueSpec());
        const deposit = validateStringNumberInRange(this.state.deposit, depositSpecification.min, depositSpecification.max)
            ? parseInt(this.state.deposit, 10)
            : null;
        const duration = getDurationFromIndex(this.state.durationIndex, durationSpecification);
        let residual = null;
        const hasResidual = isResidualEnabled(convertedResidualSpecification);
        if (hasResidual) {
            const residualMin = convertedResidualSpecification.min;
            const residualMax = convertedResidualSpecification.max;
            residual = validateStringNumberInRange(this.state.residual, residualMin, residualMax)
                ? parseInt(this.state.residual, 10) / 100
                : null;
        }
        this.props.dispatchStoreAction(StoreAction.PAYMENT_UPDATE_FINANCING_INFORMATION, {
            loanDeposit: deposit,
            loanDuration: duration,
            loanResidual: residual,
        }, (state) => {
            if (callback) {
                callback(state);
            }
        });
    }
    render() {
        const loanDetails = getLoanDetails(this.props.orderOptions, this.props.paymentLookup);
        const depositSpecification = loanDetails.getDownPaymentSpec();
        const durationSpecification = loanDetails.getDurationSpec();
        const convertedResidualSpecification = getConvertedResidualSpecification(loanDetails.getResidualValueSpec());
        const options = getAllDurationSteps(durationSpecification).map((s) => `${s} mån`);
        const optionItems = options.map((o, index) => (React.createElement("option", { key: index }, o)));
        const durationValue = options[this.state.durationIndex];
        let residual = null;
        let residualMin = null;
        let residualMax = null;
        let residualStep = null;
        let residualFixed = null;
        const hasResidual = isResidualEnabled(convertedResidualSpecification);
        const hasFixedResidual = isResidualFixed(convertedResidualSpecification);
        if (hasResidual) {
            residual = this.state.residual;
            residualMin = convertedResidualSpecification.min;
            residualMax = convertedResidualSpecification.max;
            residualStep = convertedResidualSpecification.step;
        }
        if (hasFixedResidual) {
            residualFixed = convertedResidualSpecification.min;
        }
        const hasDepositError = !validateStringNumberInRange(this.state.deposit, depositSpecification.min, depositSpecification.max);
        const hasResidualError = hasResidual &&
            !validateStringNumberInRange(residual, residualMin, residualMax);
        const hasAnyError = hasDepositError || hasResidualError;
        const isDepositDisabled = (hasAnyError && !hasDepositError) ||
            this.props.isWaitingForResponse;
        const isDurationDisabled = hasAnyError || this.props.isWaitingForResponse;
        const isResidualDisabled = (hasAnyError && !hasResidualError) ||
            this.props.isWaitingForResponse;
        const paymentOption = this.props.orderOptions
            .getPaymentOptions()
            .find((p) => p.type === PaymentType.Loan);
        const scaledImage = addSizeQuery(paymentOption.logo, 100, 60);
        const formattedPrice = formatPrice(loanDetails.getCosts().monthlyCost);
        const formattedInterest = formatPercentage(loanDetails.getInterests().interest);
        const formattedEffectiveInterest = formatPercentage(loanDetails.getInterests().effectiveInterest);
        const formattedSetupFee = formatPrice(loanDetails.getFees().setupFee);
        const formattedAdministrationFee = formatPrice(loanDetails.getFees().administrationFee);
        const formattedTotalCreditCost = formatPrice(loanDetails.getCosts().totalCreditCost);
        const formattedTotalResidualValue = formatPrice(loanDetails.getTotalResidualValue());
        const formattedCreditAmount = formatPrice(loanDetails.getCreditAmount());
        const creditPercentage = !!loanDetails.getPrice()
            ? loanDetails.getCreditAmount() / loanDetails.getPrice()
            : 0;
        const formattedCreditPercentage = formatPercentage(creditPercentage);
        const publicUrl = createFinancialProviderLink(loanDetails, this.props.vehicle.title);
        const onDepositChange = (value) => {
            this.handleSliderChange("deposit", value);
        };
        const onDurationChange = (value) => {
            this.handleSliderChange("durationIndex", value);
        };
        const onResidualValueChange = (value) => {
            this.handleSliderChange("residual", value);
        };
        const footNote = hasResidual || hasFixedResidual
            ? `Beräknat på ${formattedInterest} % ränta (effektivt ${formattedEffectiveInterest} %) och en årlig körsträcka om 1500 mil.`
            : `Beräknat på ${formattedInterest} % ränta (effektivt ${formattedEffectiveInterest} %).`;
        const loanText = `Lån (${formattedCreditPercentage} %): ${formattedCreditAmount} kr`;
        const retailerInformation = getRetailerInformation(this.props.orderOptions);
        const retailerName = !!retailerInformation
            ? retailerInformation.name
            : "Handlaren";
        const useCreditAssessment = shouldUseCreditAssessment(this.props.data, this.props.orderOptions);
        const description = useCreditAssessment
            ? `Betala bilen med finansiering via ${paymentOption.name}. Gör din låneansökan här – och få besked direkt. Kom ihåg, köpet är inte bindande förrän du signerat det definitiva affärsförslaget som tas fram av säljaren.`
            : `Betala bilen med finansiering via ${paymentOption.name}.`;
        return (React.createElement("div", { className: "page-main" },
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { "data-ecom-columnrow": "" },
                    React.createElement("div", { className: "column" },
                        React.createElement("h1", { className: "h6 no-margin" }, "Bill\u00E5n"),
                        React.createElement("div", { className: "font-size-small" }, paymentOption.name)),
                    paymentOption.logo && (React.createElement("div", { className: "column minimal" },
                        React.createElement("img", { src: scaledImage, alt: "Logotype", className: "l-block", style: {
                                maxWidth: "100px",
                                maxHeight: "60px",
                            } })))),
                React.createElement("div", { "data-ecom-content": "", className: "m-t-half" },
                    React.createElement("p", null, description),
                    React.createElement("p", null, "Ange din t\u00E4nkta kontantinsats och hur m\u00E5nga m\u00E5nader du vill l\u00E4gga upp ditt l\u00E5n p\u00E5.")),
                React.createElement("div", { "data-ecom-columnrow": "" },
                    React.createElement("div", { className: "column" },
                        React.createElement("button", { "data-ecom-link": "font-inerit", onClick: this.props.onShowPaymentMethodChooser, className: "m-t" }, "\u00C4ndra finansiering")))),
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { "data-ecom-form": "" },
                    React.createElement("div", { className: `form-group ${hasDepositError ? " has-error" : ""}` },
                        React.createElement(HelperBoxLabel, { label: "Kontantinsats (kr)", forId: "payment-input-downpayment", title: "Hur mycket av dina egna pengar vill du l\u00E4gga?" },
                            React.createElement("p", null, `Kontantinsatsen är en del av bilens pris som du betalar med egna pengar. Den behöver vara minst 20% av priset på bilen. Kontantinsatsen betalar du senare i samband med avtalsskrivning hos ${retailerName}.`),
                            React.createElement("p", null, `Ifall du har en inbytesbil kan du betala kontantinsatsen med den. Detta kommer du överens om tillsammans med ${retailerName} vid avtalsskrivning.`)),
                        React.createElement("div", { "data-ecom-inputtext": "" },
                            React.createElement("input", { type: "text", id: "payment-input-downpayment", name: "deposit", placeholder: "Kontantinsats", value: this.state.deposit, disabled: isDepositDisabled, onChange: this.handleInputValueChange, onBlur: this.handleValueUpdated })),
                        React.createElement("div", { className: "m-t" }, !hasDepositError && (React.createElement(Slider, { min: depositSpecification.min, max: depositSpecification.max, step: depositSpecification.step, initialValue: parseInt(this.state.deposit, 10), isDisabled: isDepositDisabled, onChange: onDepositChange, onAfterChange: this.handleValueUpdated }))),
                        React.createElement("div", { className: "form-alert" },
                            "Mellan ",
                            formatPrice(depositSpecification.min),
                            "kr och ",
                            formatPrice(depositSpecification.max),
                            "kr")),
                    React.createElement("div", { className: "form-group" },
                        React.createElement(HelperLabel, { label: "Avbetalning (m\u00E5n)" }),
                        React.createElement("div", { "data-ecom-select": "", className: this.props.isWaitingForResponse
                                ? "is-disabled"
                                : "" },
                            React.createElement("select", { className: "select", value: durationValue, disabled: isDurationDisabled, onChange: this.handleDurationChange }, optionItems)),
                        React.createElement("div", { className: "m-t" },
                            React.createElement(Slider, { min: 0, max: options.length - 1, step: 1, initialValue: this.state.durationIndex, isDisabled: isDurationDisabled, onChange: onDurationChange, onAfterChange: this.handleValueUpdated }))),
                    hasResidual && (React.createElement("div", { className: `form-group ${hasResidualError ? " has-error" : ""}` },
                        React.createElement("div", { "data-ecom-columnrow": "", className: "m-b-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("label", { "data-ecom-inputlabel": "", htmlFor: "payment-input-residual", className: "no-margin" }, "Restskuld (%)")),
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" },
                                    formattedTotalResidualValue,
                                    " kr"))),
                        React.createElement("div", { "data-ecom-inputtext": "" },
                            React.createElement("input", { type: "text", id: "payment-input-residual", name: "residual", placeholder: "Restskuld", value: this.state.residual, disabled: isResidualDisabled, onChange: this.handleInputValueChange, onBlur: this.handleValueUpdated })),
                        React.createElement("div", { className: "m-t" }, !hasResidualError && (React.createElement(React.Fragment, null,
                            React.createElement(Slider, { min: residualMin, max: residualMax, step: residualStep, initialValue: parseInt(this.state.residual, 10), isDisabled: isResidualDisabled, onChange: onResidualValueChange, onAfterChange: this.handleValueUpdated })))),
                        React.createElement("div", { className: "form-alert" },
                            "Mellan ",
                            residualMin,
                            " % och ",
                            residualMax,
                            " %"))),
                    hasFixedResidual && (React.createElement("div", { className: "form-group" },
                        React.createElement("div", { "data-ecom-columnrow": "", className: "m-b-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("label", { "data-ecom-inputlabel": "", htmlFor: "payment-input-residual", className: "no-margin" }, "Restskuld (%)"))),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "m-b-half" },
                            React.createElement("div", { className: "column" },
                                residualFixed,
                                "%")))))),
            this.state.hasRequestError && (React.createElement("section", { className: "page-section" },
                React.createElement(Alert, { message: "Tyv\u00E4rr gick n\u00E5gonting fel. Prova g\u00E4rna igen om en liten stund." }))),
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { className: "h6 m-b-mini" },
                    this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedPrice),
                    " ",
                    "kr/m\u00E5n"),
                React.createElement("div", { className: "font-size-small" }, footNote),
                React.createElement("div", { className: "font-size-small" }, loanText),
                React.createElement("div", { className: "m-t-half" },
                    React.createElement("button", { "data-ecom-link": "", className: "l-block", onClick: this.handleDetailsClick }, this.state.isShowingDetails
                        ? "Färre detaljer"
                        : "Detaljer")),
                this.state.isShowingDetails && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "m-t" },
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" }, "Avbetalningsperiod")),
                            React.createElement("div", { className: "column" }, this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (durationValue))),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" }, "R\u00E4nta")),
                            React.createElement("div", { className: "column" },
                                this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedInterest),
                                " ",
                                "%")),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" },
                                    "Effektiv r\u00E4nta",
                                    " ")),
                            React.createElement("div", { className: "column" },
                                this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedEffectiveInterest),
                                " ",
                                "%")),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" }, "Uppl\u00E4ggningskostnad")),
                            React.createElement("div", { className: "column" },
                                this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedSetupFee),
                                " ",
                                "kr")),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" }, "Administrativa avgifter")),
                            React.createElement("div", { className: "column" },
                                this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedAdministrationFee),
                                " ",
                                "kr/m\u00E5n")),
                        React.createElement("div", { "data-ecom-columnrow": "", className: "repeat-m-half" },
                            React.createElement("div", { className: "column" },
                                React.createElement("div", { className: "font-medium font-size-small" }, "Total kreditkostnad")),
                            React.createElement("div", { className: "column" },
                                this.props.isWaitingForResponse ? (React.createElement(SpinnerInline, null)) : (formattedTotalCreditCost),
                                " ",
                                "kr")),
                        React.createElement("div", { "data-ecom-content": true, className: "text-dark-lighten font-size-small m-t" },
                            React.createElement("p", null, "*Det h\u00E4r \u00E4r inte den slutgiltiga offerten. R\u00E4ntan kan komma att \u00E4ndras ifall det sker justeringar i initial amorteringsplan, till\u00E4gg i utrustning eller andra \u00E4ndringar som p\u00E5verkar det initiala prisf\u00F6rslaget."),
                            React.createElement("p", null, "Om marknadsr\u00E4ntan f\u00F6r\u00E4ndras kan m\u00E5nadskostnaden komma att \u00E4ndras i motsvarande m\u00E5n. M\u00E5nadskostnaden kan ocks\u00E5 komma att p\u00E5verkas av den kreditriskklass l\u00E5ntagaren \u00E5s\u00E4tts vid en kreditbed\u00F6mning."))),
                    publicUrl && (React.createElement("div", { "data-ecom-content": "", className: "m-t" },
                        React.createElement("a", { "data-ecom-link": "", href: publicUrl, target: "_blank", rel: "noopener noreferrer" },
                            `Mer information om ${paymentOption.name}`,
                            React.createElement("i", { className: "icon-link-external m-l-half" })))))),
                React.createElement("div", { "data-ecom-alert": true, className: "m-t" },
                    React.createElement("div", { className: "alert-icon-section" },
                        React.createElement("div", { className: "alert-icon" },
                            React.createElement("i", { className: "icon-exclamation no-margin" }))),
                    React.createElement("div", { className: "alert-content" }, "Det \u00E4r inte f\u00F6rr\u00E4n i kontakt med s\u00E4ljaren som det definitiva aff\u00E4rsf\u00F6rslaget tas fram av s\u00E4ljaren och godk\u00E4nns av dig som kund. I samband med leverans signeras det finansiella avtalet och det \u00E4r f\u00F6rst d\u00E5 avtalet \u00E4r bindande."))),
            React.createElement("section", { className: "page-section page-section-bottom" },
                React.createElement(ProceedButton, { updating: this.props.isWaitingForResponse, onClick: this.handleProceedClick }))));
    }
}
export default PaymentFinancingDetails;
